import { useEffect, useMemo } from 'react';

import { CuredApiPaths } from 'models/enums';
import Card from 'pages/InsightsPage/dashboards/Card';
import { FiltersForApiState } from 'pages/InsightsPage/dashboards/filtering/types';
import useFilteredData from 'pages/InsightsPage/dashboards/filtering/useFilteredData';
import DataGrid from 'pages/InsightsPage/visualizations/DataGrid';
import {
    getHeaderName,
    getValueFormatter,
    useGetRows,
    ValueFormatTypes,
} from 'pages/InsightsPage/visualizations/DataGrid/utils';

type Props = {
    filtersForApi: FiltersForApiState;
};

// Mirrors outreach-backend's EngagementByCurationTotals (routes/reports/types.ts) —
// keep field names in sync with that interface.
type EngagementByCurationTotals = {
    total_sent: number;
    total_delivered: number;
    delivered_rate: number;
    total_opened: number;
    opened_rate: number;
    unique_opened: number;
    unique_opened_rate: number;
    total_clicked: number;
    unique_clicked: number;
    click_through_rate: number;
    click_to_open_rate: number;
    total_bounced: number;
    bounced_rate: number;
};

export const buildTotalsRow = (totals: EngagementByCurationTotals, columnFields: string[], rowCount: number) => {
    const totalsByField = (totals as unknown) as Record<string, number>;
    const row: Record<string, string | number> = { id: 'total' };
    columnFields.forEach((field) => {
        if (field === 'send_name') {
            row[field] = `Total Sends: ${rowCount}`;
        } else if (field in totalsByField) {
            row[field] = totalsByField[field];
        } else {
            row[field] = '';
        }
    });
    return row;
};

const EngagementByCurationCard = ({ filtersForApi }: Props) => {
    const { getData, isError, isLoading, data } = useFilteredData({
        key: 'curations-dashboard/engagement-by-curation',
        path: CuredApiPaths.REPORT_CURATION_ENGAGEMENT,
    });

    useEffect(() => {
        getData(filtersForApi);
    }, [filtersForApi, getData]);

    const tableData = data?.rows as {}[];
    const totals = data?.totals as EngagementByCurationTotals;

    const columns: any = useMemo(() => {
        if (!tableData?.length) return;

        const columnFields = Object.keys(tableData?.[0]);
        const columns = columnFields?.map((field) => ({
            field,
            headerName: getHeaderName(field),
            valueFormatter: ({ value }: { value?: string }) => {
                const getType = () => {
                    if (field.includes('rate')) return ValueFormatTypes.PERCENTAGE;
                    return null;
                };
                return getValueFormatter({ type: getType(), value });
            },
            width: field === 'campaign_name' ? 300 : 100,
        }));

        return columns;
    }, [tableData]);

    const rows = useGetRows(tableData);

    const pinnedRows = useMemo(() => {
        if (!tableData?.length || !totals) return undefined;
        return { bottom: [buildTotalsRow(totals, Object.keys(tableData[0]), tableData.length)] };
    }, [tableData, totals]);

    return (
        <Card isError={isError} isLoading={isLoading} label="Engagement by Curation">
            <DataGrid columns={columns} hideFooter pinnedRows={pinnedRows} rows={rows} />
        </Card>
    );
};

export default EngagementByCurationCard;
